import { useEffect, useRef, useState } from "react";
import { Eraser, FileDown, PenLine, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, fileUrl, formatApiError, uploadImage } from "@/lib/api";
import { ContractSummary } from "@/components/ContractSummary";
import { SignaturePad } from "@/components/SignaturePad";
import { IdDocSlot } from "@/components/IdDocSlot";

function DocImage({ path, alt, testid, cls }) {
  if (!path) {
    return <div className="rounded-xl border border-dashed w-full h-32 grid place-items-center text-xs text-[var(--muted)] bg-[var(--surface-2)] text-center px-4" data-testid={`${testid}-hidden`}>Visibile solo all'amministratore</div>;
  }
  return <img src={fileUrl(path)} alt={alt} className={`rounded-xl border w-full max-h-64 object-contain ${cls}`} data-testid={testid} />;
}

function SignedView({ appt }) {
  const s = appt.signature;
  const [busy, setBusy] = useState(false);
  const downloadPdf = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/appointments/${appt.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contratto_${appt.client_name.replace(/\W+/g, "_")}_${appt.date}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success("PDF generato");
    } catch (e) {
      toast.error("Errore nella generazione del PDF");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl bg-white border border-emerald-200 p-5 sm:p-6 space-y-5" data-testid="signed-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 font-heading text-lg font-semibold text-emerald-700"><CheckCircle2 className="h-5 w-5" /> Firmato da {s.signer_name}</p>
        <button onClick={downloadPdf} disabled={busy} className="btn-primary h-11 px-5" data-testid="export-pdf-button">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileDown className="h-5 w-5" />} Scarica PDF
        </button>
      </div>
      <p className="text-sm text-[var(--muted)]">Firmato il {new Date(s.signed_at).toLocaleString("it-IT")} · raccolto da {s.collected_by}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {(s.id_doc_path || s.has_id_doc) && <div><p className="eyebrow mb-2">Documento – fronte</p><DocImage path={s.id_doc_path} alt="Documento fronte" testid="signed-id-photo" cls="bg-[var(--surface-2)]" /></div>}
        {(s.id_doc_back_path || s.has_id_doc_back) && <div><p className="eyebrow mb-2">Documento – retro</p><DocImage path={s.id_doc_back_path} alt="Documento retro" testid="signed-id-back-photo" cls="bg-[var(--surface-2)]" /></div>}
        {!(s.id_doc_path || s.has_id_doc || s.id_doc_back_path || s.has_id_doc_back) && <div><p className="eyebrow mb-2">Documento</p><div className="rounded-xl border border-dashed w-full h-32 grid place-items-center text-xs text-[var(--muted)] bg-[var(--surface-2)]" data-testid="signed-no-id-doc">Documento non fornito</div></div>}
        <div><p className="eyebrow mb-2">Firma</p><DocImage path={s.signature_path} alt="Firma" testid="signed-signature-image" cls="bg-white" /></div>
      </div>
    </div>
  );
}

function LegalBox({ title, text, checked, onChecked, testid }) {
  return (
    <div className="space-y-2">
      <p className="eyebrow">{title}</p>
      <div className="max-h-40 overflow-y-auto rounded-xl bg-[var(--surface-2)] p-4 text-sm whitespace-pre-line" data-testid={`${testid}-text`}>{text}</div>
      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox checked={checked} onCheckedChange={(v) => onChecked(!!v)} data-testid={`${testid}-checkbox`} className="mt-0.5 h-5 w-5 border-[var(--ink)] data-[state=checked]:bg-[var(--primary)] data-[state=checked]:border-[var(--primary)] data-[state=checked]:text-white" />
        <span className="text-sm">Il cliente dichiara di aver letto e accettato {title.toLowerCase()}.</span>
      </label>
    </div>
  );
}

export function ContractSection({ appt, dirty, onSave, onSigned, readOnly }) {
  const [settings, setSettings] = useState(null);
  const [signer, setSigner] = useState(appt.client_name);
  const [idPath, setIdPath] = useState(null);
  const [idBackPath, setIdBackPath] = useState(null);
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const padRef = useRef(null);

  useEffect(() => { api.get("/settings").then(({ data }) => setSettings(data)); }, []);

  if (appt.status === "firmato") return <div className="space-y-6"><ContractSummary appt={appt} /><SignedView appt={appt} /></div>;
  if (readOnly) return <div className="space-y-6"><ContractSummary appt={appt} /><p className="text-sm text-[var(--muted)]" data-testid="sign-readonly-note">Il tuo ruolo consente solo la consultazione: la firma va raccolta da un operatore o responsabile.</p></div>;
  if (dirty) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 space-y-3" data-testid="unsaved-warning">
        <p>Ci sono modifiche non salvate. Salva il preventivo prima di procedere alla firma.</p>
        <button className="btn-primary h-11 px-5" onClick={onSave} data-testid="save-before-sign-button">Salva ora</button>
      </div>
    );
  }
  if (!appt.items.length) return <div className="rounded-2xl border border-dashed p-10 text-center text-[var(--muted)]" data-testid="no-items-warning">Aggiungi almeno un oggetto nella scheda “Oggetti & prezzi”.</div>;

  const canSign = signer.trim() && terms && privacy && hasInk && !submitting;

  const submit = async () => {
    setSubmitting(true);
    try {
      const blob = await padRef.current.toBlob();
      const sigPath = await uploadImage(blob, "firma.png", "sensitive");
      const { data } = await api.post(`/appointments/${appt.id}/sign`, { signer_name: signer.trim(), signature_path: sigPath, id_doc_path: idPath, id_doc_back_path: idBackPath, accepted_terms: terms, accepted_privacy: privacy });
      toast.success("Firma registrata, contratto concluso");
      onSigned(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start">
      <ContractSummary appt={appt} />
      <div className="rounded-2xl bg-white border border-[var(--border)] p-5 sm:p-6 space-y-6" data-testid="signature-section">
        <h2 className="font-heading text-xl font-semibold flex items-center gap-2"><PenLine className="h-5 w-5 text-[var(--primary)]" /> Firma digitale</h2>
        <div className="space-y-1.5"><Label>Nome e cognome del firmatario</Label><Input value={signer} onChange={(e) => setSigner(e.target.value)} data-testid="signer-name-input" /></div>
        <div className="space-y-2">
          <p className="eyebrow">Carta d'identità o patente – fronte e retro (facoltativo)</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <IdDocSlot label="Fronte" path={idPath} onPath={setIdPath} testid="front" />
            <IdDocSlot label="Retro" path={idBackPath} onPath={setIdBackPath} testid="back" />
          </div>
        </div>
        {settings && <LegalBox title="Le condizioni del contratto" text={settings.conditions_text} checked={terms} onChecked={setTerms} testid="terms" />}
        {settings && <LegalBox title="L'informativa privacy" text={settings.privacy_text} checked={privacy} onChecked={setPrivacy} testid="privacy" />}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <p className="eyebrow">Firma del cliente</p>
            <button onClick={() => padRef.current.clear()} className="btn-ghost h-8 px-3 text-xs" data-testid="clear-signature-button"><Eraser className="h-4 w-4" /> Cancella</button>
          </div>
          <SignaturePad ref={padRef} onChange={setHasInk} />
        </div>
        <button onClick={submit} disabled={!canSign} className="btn-primary w-full h-12" data-testid="confirm-signature-button">
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Conferma e firma contratto"}
        </button>
        {!canSign && !submitting && <p className="text-xs text-[var(--muted)]" data-testid="sign-requirements">Servono: nome, entrambe le accettazioni e la firma. Le foto del documento sono facoltative.</p>}
      </div>
    </div>
  );
}
